import * as THREE from 'three'
import './style.css'
import { buildWorldFromDescriptor, type WorldData } from './worldbuilder'
import { CollisionWorld } from './collision'
import { makePlayerState, respawnPlayer, simulatePlayer } from './player'
import { createInput, type InputController } from './input'
import { HUD } from './hud'
import { Run } from './run'
import {
  fetchMap,
  MapNotFoundError,
  MapParseError,
  MapServerUnreachableError,
} from './mapclient'
import { mountMapBrowser } from './mapbrowser'
import { Recorder } from './recorder'
import {
  TICK_INTERVAL,
  TICK_INTERVAL_MS,
  PLAYER_RADIUS,
  PLAYER_EYE_HEIGHT,
  HORIZONTAL_FOV_DEG,
  WORLD_FLOOR_Y,
} from './constants'

const canvas = document.getElementById('game') as HTMLCanvasElement
const overlay = document.getElementById('overlay') as HTMLDivElement
const overlayCard = document.getElementById('overlay-card') as HTMLDivElement
const noclipBadge = document.getElementById('noclip-badge') as HTMLDivElement

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  powerPreference: 'high-performance',
  stencil: false,
})
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

const camera = new THREE.PerspectiveCamera(75, 1, 1, 32768)
camera.rotation.order = 'YXZ'
const EYE_OFFSET_Y = PLAYER_EYE_HEIGHT - PLAYER_RADIUS

const overlayCardOriginalHTML = overlayCard.innerHTML

function applyFov(): void {
  const hfov = (HORIZONTAL_FOV_DEG * Math.PI) / 180
  const vfov = 2 * Math.atan(Math.tan(hfov / 2) / camera.aspect)
  camera.fov = (vfov * 180) / Math.PI
  camera.updateProjectionMatrix()
}

function resize(): void {
  const w = window.innerWidth
  const h = window.innerHeight
  renderer.setSize(w, h, false)
  camera.aspect = w / h
  applyFov()
}
resize()
window.addEventListener('resize', resize)

function showErrorOverlay(message: string, onRetry: () => void): void {
  overlayCard.innerHTML = `
    <h1>Map server error</h1>
    <p>${escapeHtml(message)}</p>
    <p class="dim small">Run <code>make server</code> on port 8088.</p>
    <button type="button" id="retry-btn">Retry</button>
    <div id="maps-panel"></div>
  `
  overlay.classList.remove('hidden')
  const btn = document.getElementById('retry-btn') as HTMLButtonElement | null
  btn?.addEventListener('click', (e) => {
    e.stopPropagation()
    onRetry()
  })
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&#39;'
  )
}

function describeFetchError(err: unknown): string {
  if (err instanceof MapNotFoundError) {
    return `Map "${err.mapName}" not found on server.`
  }
  if (err instanceof MapServerUnreachableError) {
    return 'Map server unreachable. Run `make server` on port 8088.'
  }
  if (err instanceof MapParseError) {
    return 'Map server returned invalid JSON.'
  }
  return err instanceof Error ? err.message : 'Unknown error.'
}

function chosenMapName(): string {
  const params = new URLSearchParams(window.location.search)
  return params.get('map') || 'playtest'
}

async function boot(): Promise<void> {
  let world: WorldData
  const mapName = chosenMapName()
  try {
    const descriptor = await fetchMap(mapName)
    world = buildWorldFromDescriptor(descriptor)
  } catch (err) {
    showErrorOverlay(describeFetchError(err), () => {
      // Restore original overlay content + re-boot.
      overlayCard.innerHTML = overlayCardOriginalHTML
      wireOverlayClickAndSettings(null)
      void boot()
    })
    mountMapsPanel(mapName)
    return
  }

  overlayCard.innerHTML = overlayCardOriginalHTML
  mountMapsPanel(mapName)

  const collisionWorld = new CollisionWorld(world.collisionMeshes)
  const playerState = makePlayerState(world.spawnPosition)
  const prevPosition = world.spawnPosition.clone()
  const hud = new HUD()
  const run = new Run()
  const recorder = new Recorder()
  const input = createInput(canvas, (locked) => {
    overlay.classList.toggle('hidden', locked)
  })

  wireOverlayClickAndSettings(input)

  const interpPos = new THREE.Vector3()
  let accumulator = 0
  let lastMs = performance.now()

  const nowSec = (): number => performance.now() / 1000

  window.addEventListener('keydown', (e) => {
    if (e.code !== 'F9') return
    e.preventDefault()
    if (recorder.isRecording) {
      recorder.export()
    } else {
      recorder.start(world.spawnPosition)
    }
  })

  const tickPhysics = (): void => {
    input.consumeTickEdges()
    prevPosition.copy(playerState.position)
    simulatePlayer(playerState, input.state, collisionWorld, TICK_INTERVAL)
    recorder.tick(input.state)

    if (input.state.wantsReset || playerState.position.y < WORLD_FLOOR_Y) {
      input.state.wantsReset = false
      respawnPlayer(playerState, world.spawnPosition)
      prevPosition.copy(world.spawnPosition)
      run.reset()
      return
    }

    run.tick(playerState, world, nowSec())
  }

  const frame = (): void => {
    const ms = performance.now()
    let dt = ms - lastMs
    lastMs = ms
    if (dt > 250) dt = 250
    accumulator += dt

    while (accumulator >= TICK_INTERVAL_MS) {
      tickPhysics()
      accumulator -= TICK_INTERVAL_MS
    }

    const alpha = accumulator / TICK_INTERVAL_MS
    interpPos.lerpVectors(prevPosition, playerState.position, alpha)
    camera.position.set(interpPos.x, interpPos.y + EYE_OFFSET_Y, interpPos.z)
    camera.rotation.set(input.state.pitch, input.state.yaw, 0, 'YXZ')

    const v = playerState.velocity
    hud.update(run, nowSec(), Math.hypot(v.x, v.z))
    noclipBadge.hidden = !playerState.noclip

    renderer.render(world.scene, camera)
    requestAnimationFrame(frame)
  }

  requestAnimationFrame(frame)
}

function mountMapsPanel(currentMap: string): void {
  const panel = document.getElementById('maps-panel')
  if (panel) mountMapBrowser(panel, currentMap)
}

// Sensitivity slider + overlay click → pointer lock. Idempotent: re-wires on boot/retry.
let overlayClickListener: ((e: MouseEvent) => void) | null = null
function wireOverlayClickAndSettings(input: InputController | null): void {
  if (overlayClickListener) overlay.removeEventListener('click', overlayClickListener)
  overlayClickListener = () => canvas.requestPointerLock()
  overlay.addEventListener('click', overlayClickListener)

  if (!input) return

  const sensInput = document.getElementById('sens') as HTMLInputElement | null
  const sensValue = document.getElementById('sens-value') as HTMLSpanElement | null
  const settingsEl = document.getElementById('settings') as HTMLDivElement | null
  if (!sensInput || !sensValue || !settingsEl) return

  const renderSens = (v: number): void => { sensValue.textContent = v.toFixed(2) }
  sensInput.value = String(input.getSensitivity())
  renderSens(input.getSensitivity())
  sensInput.addEventListener('input', () => {
    const v = parseFloat(sensInput.value)
    input.setSensitivity(v)
    renderSens(input.getSensitivity())
  })
  for (const ev of ['click', 'mousedown', 'mouseup', 'pointerdown', 'pointerup'] as const) {
    settingsEl.addEventListener(ev, (e) => e.stopPropagation())
  }
}

void boot()
